import React from 'react'
import { AcademicCap } from '@components/common/icons/custom-icons'
import { RocketLaunchIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { FastlyImage } from '@components/common/image'

const CURRICULUM_SECTIONS = [
  {
    badge: {
      icon: <AcademicCap className="h-4 w-4" />,
      text: 'Expert guidance',
      className:
        'text-blue-300 bg-blue-50 bg-opacity-25 border border-blue-100 border-opacity-75',
    },
    items: [
      {
        heading: 'Highly selective educator pool',
        description:
          'Only a small, carefully vetted group of educators is selected, resulting in a curated pool of expert instructors teaching students worldwide.',
      },
      {
        heading: 'Strong technical qualifications',
        description:
          'Instructors hold a Computer Science or related technical degree and have a minimum of 3+ years of coding teaching experience.',
      },
      {
        heading: 'Child-centered teaching skills',
        description:
          'Teachers are chosen for strong communication skills and a proven ability to teach kids with patience, clarity, and engagement.',
      },
    ],
    imageSrc: '/images/about-us/curriculum/instructors.png',
    imageAlt: 'Expert instructors teaching students',
    imageWidth: 723,
    imageHeight: 324,
    reverse: true,
  },
  {
    badge: {
      icon: <SparklesIcon className="h-4 w-4" />,
      text: 'AI - First Curriculum',
      className:
        'text-violet-100 bg-violet-50 border border-violet-100 border-opacity-25',
    },
    items: [
      {
        heading: 'AI is built into every course',
        description:
          'AI concepts evolve gradually, from elementary to high school, building a deeper understanding at each step.',
      },
      {
        heading: 'Hands-on, project-based learning',
        description:
          'Students learn by building real projects—animations, games, apps, websites, and AI-powered creations.',
      },
      {
        heading: 'Progressive AI learning journey',
        description:
          'AI concepts evolve gradually, from elementary to high school, building a deeper understanding at each step.',
      },
    ],
    imageSrc: '/images/about-us/curriculum/ai-curriculum.png',
    imageAlt: 'AI-powered curriculum with interactive projects',
    imageWidth: 740,
    imageHeight: 448,
    reverse: false,
  },
  {
    badge: {
      icon: <RocketLaunchIcon className="h-4 w-4" />,
      text: 'Smart Learning',
      className: 'text-orange bg-orange-200 border border-orange-300',
    },
    items: [
      {
        heading: 'Built in-house with purpose',
        description:
          "Codingal's platform is thoughtfully designed and continuously improved in-house, aligning closely with how children learn and teachers teach.",
      },
      {
        heading: 'Designed for smooth learning',
        description:
          'Every feature is created to remove friction, making classes easy to navigate, engaging, and intuitive for students and educators.',
      },
      {
        heading: "Clear visibility into your child's progress",
        description:
          'Parents and students can track learning progress, completed projects, and milestones through a simple and transparent experience.',
      },
    ],
    imageSrc: '/images/about-us/curriculum/technology.png',
    imageAlt: 'Scalable technology platform',
    imageWidth: 686,
    imageHeight: 445,
    reverse: true,
  },
]

export interface AlternatingSectionProps {
  badge: {
    icon: React.ReactNode
    text: string
    className?: string
  }
  items: {
    heading: string
    description: string
  }[]
  imageSrc: string
  imageAlt: string
  imageWidth: number
  imageHeight: number
  reverse?: boolean
}

export const AlternatingSection: React.FC<AlternatingSectionProps> = ({
  badge,
  items,
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col-reverse gap-6 ${
        reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
      } items-center`}
    >
      {/* Text Content */}
      <div className="flex-1 flex flex-col items-start text-left gap-3">
        {/* Badge */}
        <div
          className={`inline-flex items-center px-4 py-2 w-auto rounded-full text-sm gap-2 ${badge.className}`}
        >
          <span className="flex items-center">{badge.icon}</span>
          <h3>{badge.text}</h3>
        </div>

        {/* Items */}
        <div className="flex flex-col gap-3">
          {items.map((item, index) => (
            <div key={index} className="flex flex-col gap-2">
              <h4 className="text-lg md:text-xl font-600">{item.heading}</h4>
              <p className="text-base md:text-lg leading-tight">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Image Content */}
      <div className="flex-1 flex justify-center">
        {/* Desktop Image */}
        <div>
          <FastlyImage
            src={imageSrc}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            className="w-full h-auto"
          />
        </div>
      </div>
    </div>
  )
}

const Curriculum: React.FC = () => {
  return (
    <div className="px-5 py-12 lg:py-16 xl:pb-6 bg-white">
      <div className="container mx-auto relative">
        {/* Header Section */}
        <FastlyImage
          src="/images/about-us/assets/penguin-umbrella.png"
          alt="Penguin Umbrella"
          height={128}
          width={122}
          className="absolute top-0 left-0 -mt-3 -ml-6 xl:-ml-3 hidden lg:block"
        />
        <FastlyImage
          src="images/about-us/assets/small-cloud.png"
          height={37}
          width={111}
          alt="Cloud"
          className="transform self-start absolute -ml-6 top-5 p-3 mt-3 pb-5 hidden lg:block"
        />
        <div className="text-center mb-10 lg:mb-12 max-w-4xl mx-auto">
          <h2 className="pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
            A learning community built with care
          </h2>
          <p className="text-lg text-grey mx-auto leading-tight">
            From how we train educators to how we design curriculum and tools,
            every part of Codingal is built to support great learning outcomes.
          </p>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-6">
          {CURRICULUM_SECTIONS.map((section, index) => (
            <AlternatingSection
              key={index}
              badge={section.badge}
              items={section.items}
              imageSrc={section.imageSrc}
              imageAlt={section.imageAlt}
              imageWidth={section.imageWidth}
              imageHeight={section.imageHeight}
              reverse={section.reverse}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Curriculum
